// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import { extent, max, min } from 'd3-array';
import Zion from './utils/Zion';
import { linearAxis, timeAxis } from '../../../factory/axes';

// polyfills
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;

const margins = {
  top: 30,
  right: 70,
  bottom: 25,
  left: 40,
};

const parser = d3.timeParse('%Y-%m-%d'); // https://github.com/d3/d3-time-format;

let minValue;
let maxValue;

let minDomain;
let maxDomain;

let metaData;
let lastData;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const pTime = d3.timeParse('%Y-%m-%d');

stylePremium(false, false);

const rParser = d3.timeParse('%m/%d/%Y');
const recessionData = [
  { start: '3/1/2001', end: '11/1/2001' },
  // { start: '12/1/2007', end: '6/1/2009', label: 'Recessions' },
  { start: '2/1/2020', end: '4/1/2020', label: 'Covid-19 Recession' },
];

recessionData.forEach((d) => {
  d.start = rParser(d.start);
  d.end = rParser(d.end);
});

d3.json('https://ix.cnn.io/data/interest-rates/interest_rates.json').then((data) => {
  chartData = data.FEDFUNDS.data.map(d => ({
    date: d3.timeParse('%Y-%m-%d')(d.date),
    value: +d.target_rate,
    upper_rate: +d.upper_rate,
    lower_rate: +d.lower_rate,
  }));

  metaData = data.FEDFUNDS;

  // chartData = rawData.filter(d => d.date > pTime('1983-01-01'));

  if (!d3.select('.subhed.dek span').empty()) {
    // Append headline to the subhed element using HTML
    d3.select('.subhed.dek span').html(metaData.subhead);
  } else {
    d3.select('.subhed.dek').append('span').html(metaData.subhead);
  }

  if (!d3.select('.timestamp span').empty()) {
    // Append last updated timestamp to the footnote element using HTML
    d3.select('.timestamp span').html(`Note: Data current as of ${metaData.last_updated_str}`);
  } else {
    d3.select('.timestamp').append('span').html(`Note: Data current as of ${metaData.last_updated_str}`);
  }

  console.log(chartData);

  minDomain = d3.min(chartData, d => d.date);
  maxDomain = d3.max(chartData, d => d.date);

  minValue = 0;
  maxValue = d3.max(chartData, d => d.value);

  lastData = chartData.filter(d => d.date == maxDomain)[0];

  draw();
  window.onresize = draw;
});

function draw(filteredYears = null) {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 3 : 5;
  const yTicks = isMobile ? 5 : 5;

  const yAxisOffset = -10; // Adjust this value to shift the Y-axis leftward

  let filteredData = chartData;
  let filteredMinDomain = minDomain;

  if (filteredYears) {
    try {
      filteredMinDomain = d3.timeYear.offset(maxDomain, -filteredYears);
      if (isNaN(filteredMinDomain.getTime())) {
        throw new Error('Invalid date calculated for filteredMinDomain');
      }
    } catch (error) {
      console.error('Error calculating filteredMinDomain:', error);
      filteredMinDomain = minDomain; // Fallback to minDomain if there's an issue
    }
    filteredData = chartData.filter(d => d.date >= filteredMinDomain && d.date <= maxDomain);

    console.log('Filtered Min Domain:', filteredMinDomain);
    console.log('Max Domain:', maxDomain);
    console.log('Filtered Data:', filteredData);
  }

  if (!filteredData || filteredData.length === 0) {
    console.error('No data available to plot.');
    return;
  }

  const xScale = d3.scaleTime()
    .domain([filteredMinDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(xTicks)
    .tickFormat((d, i) => {
      if (i === 0 || d3.timeFormat('%m')(d) == '01') {
        return d3.timeFormat('%b ’%y')(d);
      }
      return d3.timeFormat('%b')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat((d, i, x) => (i == x.length - 1 ? `${d3.format('.0f')(d)}%` : `${d3.format('.0f')(d)}`));

  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  // Add recession rectangles
  g.selectAll('.recession')
    .data(recessionData)
    .enter()
    .append('rect')
    .attr('class', 'recession')
    .attr('x', d => xScale(d.start))
    .attr('y', 0)
    .attr('width', d => xScale(d.end) - xScale(d.start))
    .attr('height', chartHeight)
    .attr('fill', '#F4F4F4')
    .attr('opacity', 1);

  // Add label over the middle rectangle
  const middleRecession = recessionData.find(d => d.label === 'Covid-19 Recession');
  if (middleRecession) {
    const middleX = (xScale(middleRecession.start) + xScale(middleRecession.end)) / 2;

    g.append('text')
      .attr('class', 'recession-label')
      .attr('x', middleX)
      .attr('y', 20)
      .attr('text-anchor', 'middle')
      .attr('fill', '#000')
      .text(middleRecession.label);
  }

  // Define the area generator
  const area = d3.area()
    .x(d => xScale(d.date))
    .y0(d => yScale(d.lower_rate))
    .y1(d => yScale(d.upper_rate))
    .curve(d3.curveStepAfter); // Match the curve of the line

  // Add the area path
  g.append('path')
    .datum(filteredData)
    .attr('class', 'area')
    .attr('fill', '#e9d8f2') // Very light purple
    .attr('d', area);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  // Apply Y-axis offset
  g.append('g')
    .attr('class', 'grid y')
    .attr('transform', `translate(${yAxisOffset}, 0)`) // Shift Y-axis to the left
    .call(yAxis);


  const lineGenerator = d3.line()
    .x(d => xScale(d.date)) // Set the x position (e.g., date)
    .y(d => yScale(d.value)) // Set the y position (e.g., value)
    .curve(d3.curveStepAfter); // Apply step interpolation

  // Now use this line generator for your path
  const line = g.append('g')
    .attr('class', 'line');

  line.append('path')
    .datum(filteredData)
    .attr('stroke', '#7c4ea5')
    .attr('stroke-width', '2')
    .attr('fill', 'none')
    .attr('d', lineGenerator); // Use the modified line generator


  d3.select('.grid.y .domain').remove();

  const lastPoint = filteredData.filter(d => d.date == maxDomain)[0];
  const annotation = g.append('g')
    .attr('class', 'annotation')
    .attr('transform', `translate(${[xScale(lastPoint.date), yScale(lastPoint.value)]})`);

  annotation.append('circle')
    .attr('r', '4')
    .attr('fill', '#7c4ea5')
    .attr('stroke', 'none');

  annotation.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-date')
    .text(d3.timeFormat('%b \'%y')(lastPoint.date));

  annotation.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-value')
    .text(`${d3.format('.3f')(lastPoint.value)}%`)
    .attr('dy', '1.2em');

  window.pymChild.sendHeight();
}


// Usage: to filter to the last 5 years, call draw(5);
draw(); // Draws full chart by default
